import { useEffect, useMemo, useState } from 'react'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import {
  downloadJobAnalyticsExport,
  fetchJobAnalyticsDrilldown,
  fetchJobAnalyticsFilters,
  fetchJobAnalyticsInsights,
} from '../../api/analyticsApi'

const DEFAULT_PRESET_DAYS = 90

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

function daysAgoIso(days) {
  const current = new Date()
  current.setDate(current.getDate() - Math.max(0, Number(days || 0)))
  return current.toISOString().slice(0, 10)
}

function toDropdownOptions(items = [], emptyLabel = 'All') {
  return [{ value: '', label: emptyLabel }, ...items.map((item) => ({ value: String(item.id), label: item.name }))]
}

export function useJobAnalytics() {
  const [projectId, setProjectId] = useState('')
  const [resourcePoolId, setResourcePoolId] = useState('')
  const [userId, setUserId] = useState('')
  const [jobStatus, setJobStatus] = useState('')
  const [priority, setPriority] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [presetDays, setPresetDays] = useState(String(DEFAULT_PRESET_DAYS))
  const [dateFrom, setDateFrom] = useState(daysAgoIso(DEFAULT_PRESET_DAYS - 1))
  const [dateTo, setDateTo] = useState(todayIso())
  const [committedFilters, setCommittedFilters] = useState({
    projectId: '',
    resourcePoolId: '',
    userId: '',
    jobStatus: '',
    priority: '',
    departmentId: '',
    dateFrom: daysAgoIso(DEFAULT_PRESET_DAYS - 1),
    dateTo: todayIso(),
  })

  const [projects, setProjects] = useState([])
  const [resourcePools, setResourcePools] = useState([])
  const [users, setUsers] = useState([])
  const [statuses, setStatuses] = useState([])
  const [priorities, setPriorities] = useState([])
  const [datePresets, setDatePresets] = useState([])
  const [departments, setDepartments] = useState([])
  const [insights, setInsights] = useState(null)
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [drilldown, setDrilldown] = useState(null)
  const [drillLevel, setDrillLevel] = useState('job')
  const [drillTitle, setDrillTitle] = useState('Job Details')
  const [drillPeriodFrom, setDrillPeriodFrom] = useState(null)
  const [drillPeriodTo, setDrillPeriodTo] = useState(null)
  const [isLoadingDrilldown, setIsLoadingDrilldown] = useState(false)
  const [drilldownError, setDrilldownError] = useState('')
  const [isExporting, setIsExporting] = useState(false)

  const [isLoadingFilters, setIsLoadingFilters] = useState(true)
  const [isLoadingInsights, setIsLoadingInsights] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    async function loadFilters() {
      setIsLoadingFilters(true)
      try {
        const payload = await fetchJobAnalyticsFilters({
          projectId: projectId || undefined,
          resourcePoolId: resourcePoolId || undefined,
          userId: userId || undefined,
          departmentId: departmentId || undefined,
        })
        if (!active) return
        setProjects(Array.isArray(payload?.projects) ? payload.projects : [])
        setResourcePools(Array.isArray(payload?.resourcePools) ? payload.resourcePools : [])
        setUsers(Array.isArray(payload?.users) ? payload.users : [])
        setStatuses(Array.isArray(payload?.statuses) ? payload.statuses : [])
        setPriorities(Array.isArray(payload?.priorities) ? payload.priorities : [])
        setDatePresets(Array.isArray(payload?.datePresets) ? payload.datePresets : [])
        setDepartments(Array.isArray(payload?.departments) ? payload.departments : [])
      } catch (requestError) {
        if (!active) return
        setProjects([])
        setResourcePools([])
        setUsers([])
        setStatuses([])
        setPriorities([])
        setDatePresets([])
        setError(extractApiErrorMessage(requestError, 'Failed to load job analytics filters.'))
      } finally {
        if (active) setIsLoadingFilters(false)
      }
    }
    loadFilters()
    return () => {
      active = false
    }
  }, [projectId, resourcePoolId, userId, departmentId])

  useEffect(() => {
    if (!projectId) return
    const isValid = projects.some((item) => String(item.id) === String(projectId))
    if (!isValid) {
      setProjectId('')
      setResourcePoolId('')
      setCommittedFilters((prev) => ({
        ...prev,
        projectId: '',
        resourcePoolId: '',
      }))
    }
  }, [projectId, projects])

  useEffect(() => {
    if (!resourcePoolId) return
    const isValid = resourcePools.some((item) => String(item.id) === String(resourcePoolId))
    if (!isValid) {
      setResourcePoolId('')
      setCommittedFilters((prev) => ({
        ...prev,
        resourcePoolId: '',
      }))
    }
  }, [resourcePoolId, resourcePools])

  useEffect(() => {
    let active = true
    async function loadInsights() {
      setIsLoadingInsights(true)
      setError('')
      try {
        const payload = await fetchJobAnalyticsInsights({
          projectId: committedFilters.projectId || undefined,
          resourcePoolId: committedFilters.resourcePoolId || undefined,
          userId: committedFilters.userId || undefined,
          jobStatus: committedFilters.jobStatus || undefined,
          priority: committedFilters.priority || undefined,
          departmentId: committedFilters.departmentId || undefined,
          dateFrom: committedFilters.dateFrom || undefined,
          dateTo: committedFilters.dateTo || undefined,
        })
        if (!active) return
        setInsights(payload || {})
      } catch (requestError) {
        if (!active) return
        setInsights(null)
        setError(extractApiErrorMessage(requestError, 'Failed to load job analytics insights.'))
      } finally {
        if (active) setIsLoadingInsights(false)
      }
    }
    loadInsights()
    return () => {
      active = false
    }
  }, [committedFilters])

  function commitFilters(nextFilters = {}) {
    setCommittedFilters((previous) => ({
      ...previous,
      ...nextFilters,
    }))
  }

  function updateProjectId(value) {
    const normalizedValue = String(value || '')
    setProjectId(normalizedValue)
    commitFilters({ projectId: normalizedValue })
  }

  function updateResourcePoolId(value) {
    const normalizedValue = String(value || '')
    setResourcePoolId(normalizedValue)
    commitFilters({ resourcePoolId: normalizedValue })
  }

  function updateFilter(key, value, setter) {
    const normalizedValue = String(value || '')
    setter(normalizedValue)
    commitFilters({ [key]: normalizedValue })
  }

  function applyPreset(daysValue) {
    const parsed = Number(daysValue || 30)
    const normalizedDays = Number.isFinite(parsed) && parsed > 0 ? parsed : 30
    const nextDateTo = todayIso()
    const nextDateFrom = daysAgoIso(normalizedDays - 1)
    setPresetDays(String(normalizedDays))
    setDateTo(nextDateTo)
    setDateFrom(nextDateFrom)
    commitFilters({ dateFrom: nextDateFrom, dateTo: nextDateTo })
  }

  function resetFilters() {
    setProjectId('')
    setResourcePoolId('')
    setUserId('')
    setJobStatus('')
    setPriority('')
    setDepartmentId('')
    applyPreset(String(DEFAULT_PRESET_DAYS))
    const reset = {
      projectId: '',
      resourcePoolId: '',
      userId: '',
      jobStatus: '',
      priority: '',
      departmentId: '',
      dateFrom: daysAgoIso(DEFAULT_PRESET_DAYS - 1),
      dateTo: todayIso(),
    }
    setCommittedFilters(reset)
  }

  const projectOptions = useMemo(() => toDropdownOptions(projects, 'All Projects'), [projects])
  const resourcePoolOptions = useMemo(() => toDropdownOptions(resourcePools, 'All RPs'), [resourcePools])
  const userOptions = useMemo(() => toDropdownOptions(users, 'All Resources'), [users])
  const statusOptions = useMemo(() => [{ value: '', label: 'All Statuses' }, ...statuses.map((value) => ({ value, label: value }))], [statuses])
  const priorityOptions = useMemo(() => [{ value: '', label: 'All Priorities' }, ...priorities.map((value) => ({ value, label: value }))], [priorities])
  const departmentOptions = useMemo(() => toDropdownOptions(departments, 'All Departments'), [departments])
  const presetOptions = useMemo(
    () =>
      (datePresets.length ? datePresets : [{ label: 'Last 90 Days', days: DEFAULT_PRESET_DAYS }]).map((item) => ({
        value: String(item.days),
        label: item.label,
      })),
    [datePresets],
  )

  function queryParams(overrides = {}) {
    // When a specific chart period was clicked, narrow dateFrom/dateTo to that bucket's
    // exact boundaries instead of sending the full dashboard filter range.
    const periodFrom = drillPeriodFrom || committedFilters.dateFrom
    const periodTo = drillPeriodTo || committedFilters.dateTo
    return {
      projectId: committedFilters.projectId || undefined,
      resourcePoolId: committedFilters.resourcePoolId || undefined,
      userId: committedFilters.userId || undefined,
      jobStatus: committedFilters.jobStatus || undefined,
      priority: committedFilters.priority || undefined,
      departmentId: committedFilters.departmentId || undefined,
      dateFrom: periodFrom || undefined,
      dateTo: periodTo || undefined,
      ...overrides,
    }
  }

  async function openDrilldown(title = 'Job Details', level = 'job', periodFrom = null, periodTo = null) {
    setIsDetailsOpen(true)
    setDrillTitle(title)
    setDrillLevel(level)
    setDrillPeriodFrom(periodFrom)
    setDrillPeriodTo(periodTo)
    setIsLoadingDrilldown(true)
    setDrilldownError('')
    try {
      const periodOverrides = periodFrom && periodTo ? { dateFrom: periodFrom, dateTo: periodTo } : {}
      setDrilldown(await fetchJobAnalyticsDrilldown(queryParams({ level, ...periodOverrides })))
    } catch (requestError) {
      setDrilldown(null)
      setDrilldownError(extractApiErrorMessage(requestError, 'Failed to load drill-down data.'))
    } finally {
      setIsLoadingDrilldown(false)
    }
  }

  async function exportCurrentDrilldown(format = 'csv') {
    setIsExporting(true)
    try {
      const blob = await downloadJobAnalyticsExport(queryParams({ level: drillLevel, format }))
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `job_analytics_${drillLevel}.${format}`
      anchor.click()
      window.URL.revokeObjectURL(url)
    } catch (requestError) {
      setDrilldownError(extractApiErrorMessage(requestError, 'Failed to export job analytics.'))
    } finally {
      setIsExporting(false)
    }
  }

  return {
    state: {
      projectId,
      resourcePoolId,
      userId,
      jobStatus,
      priority,
      departmentId,
      presetDays,
      dateFrom,
      dateTo,
      projectOptions,
      resourcePoolOptions,
      userOptions,
      statusOptions,
      priorityOptions,
      departmentOptions,
      presetOptions,
      insights,
      isDetailsOpen,
      drilldown,
      drillLevel,
      drillTitle,
      drillPeriodFrom,
      drillPeriodTo,
      isLoadingDrilldown,
      drilldownError,
      isExporting,
      isLoading: isLoadingFilters || isLoadingInsights,
      isLoadingFilters,
      isLoadingInsights,
      error,
    },
    actions: {
      setProjectId: updateProjectId,
      setResourcePoolId: updateResourcePoolId,
      setUserId: (value) => updateFilter('userId', value, setUserId),
      setJobStatus: (value) => updateFilter('jobStatus', value, setJobStatus),
      setPriority: (value) => updateFilter('priority', value, setPriority),
      setDepartmentId: (value) => updateFilter('departmentId', value, setDepartmentId),
      setDateFrom,
      setDateTo,
      applyPreset,
      resetFilters,
      openDetails: openDrilldown,
      openTimesheets: (periodFrom, periodTo) => openDrilldown('Job Activity Details', 'timesheet', periodFrom, periodTo),
      openTasks: (periodFrom, periodTo) => openDrilldown('Task Details', 'task', periodFrom, periodTo),
      exportCurrentDrilldown,
      closeDetails: () => {
        setIsDetailsOpen(false)
        setDrillPeriodFrom(null)
        setDrillPeriodTo(null)
      },
    },
  }
}
